import { useState, useEffect } from 'react';
import type { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Lock, User, AlertCircle, Eye, EyeOff,
  MapPin, Layers, ChevronRight, ArrowLeft,
  BarChart3, Globe2, Shield, Workflow,
  Building2, CheckCircle2,
  Loader2, CalendarRange, GitMerge,
} from 'lucide-react';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { loginStart, loginSuccess, loginFailure } from '@/state/authSlice';
import { setSession } from '@/state/sessionSlice';
import { authApi } from '@/api/auth.api';
import { apiClient } from '@/api/client';
import { AppRoutes } from '@/constants/routes';
import { broadcastCompanyChange } from '@/utils/crossTabSync';
import type { ContextLocation, ContextCostCenter, ContextCompany, LoginResponse, SelectContextRequest } from '@/types/auth.types';
import { costCentresForLocation, defaultCostCentreFor } from '@/utils/costCentreScope';
import type { FinancialYearListDto } from '@/types/financialYear.types';

// ── Schema ───────────────────────────────────────────────────────────────────
// Company is deliberately absent: ENH-002 asks for a company SELECTION, and the operator cannot
// select what they have not authenticated for yet. It is resolved from the credentials in step 1
// and chosen in step 2 — see resolveCompanies below.
const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});
type LoginForm = z.infer<typeof loginSchema>;

// Changed based on Dinisuru's request: localStorage is kept clean; company codes are not persisted.
try {
  localStorage.removeItem('myledger_last_company_code');
} catch {
  /* ignore */
}

// ── Static data ───────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: Building2,    title: 'Multi-Entity',        desc: 'Consolidation across companies'  },
  { icon: BarChart3,    title: 'Real-time P&L',       desc: 'Live financial insights'          },
  { icon: Workflow,     title: 'Workflow Approvals',  desc: 'Maker-checker journal controls'   },
  { icon: Globe2,       title: 'Multi-Currency',      desc: 'FX-aware global accounting'       },
  { icon: Shield,       title: 'Audit & Compliance',  desc: 'Full trail on every entry'        },
  { icon: GitMerge,     title: 'Ecosystem Integration', desc: 'POS · AR · AP · INV · CRM'    },
] as const;

const MODULES = ['POS', 'GL', 'AR', 'AP', 'INV', 'PAY', 'FA', 'CRM'] as const;

// ── myPOS ONE Dimensional Cube mark ──────────────────────────────────────────
// Brand spec: top=Signal Blue #3B82F6, left=Commerce Teal #0891B2,
// right=Ink Navy #0F172A (dark: Slate #334155), arrow=White.
// ViewBox cropped tightly to the cube geometry (78,68 → 162,170) so the mark
// fills the rendered size — matches the visual weight of the myPOS ONE PNG assets.
const BrandCube = ({ size = 32, dark = false }: { size?: number; dark?: boolean }) => (
  <svg width={size} height={size} viewBox="73 65 95 108" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <polygon points="120,72.1 160.87,95.7 120,119.3 79.13,95.7" fill="#3B82F6" />
    <polygon points="78.52,96.76 119.40,120.35 119.40,167.54 78.52,143.95" fill="#0891B2" />
    <polygon points="161.49,96.76 161.49,143.95 120.61,167.54 120.61,120.35" fill={dark ? '#334155' : '#0F172A'} />
    <polygon points="111.74,106.15 134.58,119.51 111.74,132.39" fill="#FFFFFF" />
  </svg>
);

// ── Dashboard Mockup SVG (brand-coloured) ─────────────────────────────────────
const DashboardMockup = () => (
  <svg
    viewBox="0 0 580 340"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-full"
    aria-hidden="true"
  >
    {/* Window chrome */}
    <rect width="580" height="340" rx="10" fill="white" />
    <rect width="580" height="30" rx="10" fill="#f1f5f9" />
    <rect y="18" width="580" height="12" fill="#f1f5f9" />
    <circle cx="16" cy="15" r="4.5" fill="#fca5a5" />
    <circle cx="30" cy="15" r="4.5" fill="#fde68a" />
    <circle cx="44" cy="15" r="4.5" fill="#86efac" />
    {/* Sidebar — Ink Navy (myPOS ONE brand) */}
    <rect x="0" y="30" width="110" height="310" fill="#0F172A" />
    {/* Active nav item — Signal Blue */}
    <rect x="8"  y="42"  width="94" height="22" rx="6" fill="#2563EB" fillOpacity="0.9" />
    <rect x="14" y="47"  width="36"  height="4"  rx="2" fill="white" fillOpacity="0.9" />
    <rect x="14" y="54"  width="24"  height="4"  rx="2" fill="white" fillOpacity="0.5" />
    <rect x="8"  y="72"  width="94" height="18" rx="5" fill="white" fillOpacity="0.05" />
    <rect x="14" y="78"  width="50"  height="4"  rx="2" fill="#9DB0CF" />
    <rect x="8"  y="96"  width="94" height="18" rx="5" fill="white" fillOpacity="0.04" />
    <rect x="14" y="102" width="40"  height="4"  rx="2" fill="#9DB0CF" />
    <rect x="8"  y="120" width="94" height="18" rx="5" fill="white" fillOpacity="0.04" />
    <rect x="14" y="126" width="55"  height="4"  rx="2" fill="#9DB0CF" />
    <rect x="8"  y="144" width="94" height="18" rx="5" fill="white" fillOpacity="0.04" />
    <rect x="14" y="150" width="45"  height="4"  rx="2" fill="#9DB0CF" />
    <rect x="8"  y="168" width="94" height="18" rx="5" fill="white" fillOpacity="0.04" />
    <rect x="14" y="174" width="35"  height="4"  rx="2" fill="#9DB0CF" />
    {/* Top bar */}
    <rect x="110" y="30" width="470" height="28" fill="#f8fafc" />
    <rect x="120" y="38" width="100" height="5"  rx="2.5" fill="#e2e8f0" />
    <rect x="530" y="36" width="40"  height="8"  rx="4"   fill="#e2e8f0" />
    {/* KPI Cards */}
    <rect x="118" y="66" width="100" height="58" rx="7" fill="white" stroke="#e2e8f0" strokeWidth="1" />
    <rect x="128" y="75" width="42" height="4" rx="2" fill="#94a3b8" />
    <rect x="128" y="84" width="64" height="9" rx="3" fill="#0F172A" />
    <rect x="128" y="98" width="38" height="4" rx="2" fill="#22c55e" fillOpacity="0.8" />
    <rect x="226" y="66" width="100" height="58" rx="7" fill="white" stroke="#e2e8f0" strokeWidth="1" />
    <rect x="236" y="75" width="42" height="4" rx="2" fill="#94a3b8" />
    <rect x="236" y="84" width="64" height="9" rx="3" fill="#0F172A" />
    <rect x="236" y="98" width="38" height="4" rx="2" fill="#2563EB" fillOpacity="0.8" />
    <rect x="334" y="66" width="100" height="58" rx="7" fill="white" stroke="#e2e8f0" strokeWidth="1" />
    <rect x="344" y="75" width="42" height="4" rx="2" fill="#94a3b8" />
    <rect x="344" y="84" width="64" height="9" rx="3" fill="#0F172A" />
    <rect x="344" y="98" width="38" height="4" rx="2" fill="#0891B2" fillOpacity="0.8" />
    <rect x="442" y="66" width="126" height="58" rx="7" fill="white" stroke="#e2e8f0" strokeWidth="1" />
    <rect x="452" y="75" width="42" height="4" rx="2" fill="#94a3b8" />
    <rect x="452" y="84" width="64" height="9" rx="3" fill="#0F172A" />
    <rect x="452" y="98" width="38" height="4" rx="2" fill="#f59e0b" fillOpacity="0.8" />
    {/* Main Chart — Signal Blue primary, Teal secondary */}
    <rect x="118" y="132" width="300" height="152" rx="7" fill="white" stroke="#e2e8f0" strokeWidth="1" />
    <rect x="128" y="142" width="90" height="6"  rx="3" fill="#0F172A" />
    <rect x="128" y="153" width="55" height="4"  rx="2" fill="#94a3b8" />
    <line x1="128" y1="186" x2="408" y2="186" stroke="#f1f5f9" strokeWidth="1" />
    <line x1="128" y1="206" x2="408" y2="206" stroke="#f1f5f9" strokeWidth="1" />
    <line x1="128" y1="226" x2="408" y2="226" stroke="#f1f5f9" strokeWidth="1" />
    <line x1="128" y1="246" x2="408" y2="246" stroke="#f1f5f9" strokeWidth="1" />
    {/* Signal Blue P&L line */}
    <polyline points="128,258 163,235 198,242 233,214 268,222 303,195 338,202 373,178 408,170"
      stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <polygon points="128,258 163,235 198,242 233,214 268,222 303,195 338,202 373,178 408,170 408,270 128,270"
      fill="#2563EB" fillOpacity="0.05" />
    {/* Teal secondary line */}
    <polyline points="128,268 163,258 198,262 233,248 268,255 303,240 338,245 373,232 408,228"
      stroke="#0891B2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.5" />
    <rect x="128" y="275" width="8" height="3" rx="1.5" fill="#2563EB" />
    <rect x="140" y="275" width="35" height="3" rx="1.5" fill="#e2e8f0" />
    <rect x="182" y="275" width="8" height="3" rx="1.5" fill="#0891B2" fillOpacity="0.7" />
    <rect x="194" y="275" width="35" height="3" rx="1.5" fill="#e2e8f0" />
    {/* Side Panel — Balance breakdown */}
    <rect x="426" y="132" width="142" height="152" rx="7" fill="white" stroke="#e2e8f0" strokeWidth="1" />
    <rect x="436" y="142" width="70" height="6" rx="3" fill="#0F172A" />
    <circle cx="497" cy="196" r="34" fill="none" stroke="#f1f5f9" strokeWidth="12" />
    <circle cx="497" cy="196" r="34" fill="none" stroke="#2563EB" strokeWidth="12"
      strokeDasharray="90 134" strokeDashoffset="0" strokeLinecap="round" />
    <circle cx="497" cy="196" r="34" fill="none" stroke="#0891B2" strokeWidth="12"
      strokeDasharray="55 134" strokeDashoffset="-90" strokeLinecap="round" />
    <circle cx="497" cy="196" r="34" fill="none" stroke="#22c55e" strokeWidth="12"
      strokeDasharray="28 134" strokeDashoffset="-145" strokeLinecap="round" />
    <rect x="481" y="191" width="32" height="6" rx="3" fill="#0F172A" />
    <rect x="485" y="200" width="24" height="3" rx="1.5" fill="#94a3b8" />
    <circle cx="440" cy="248" r="3" fill="#2563EB" />
    <rect x="446" y="246" width="30" height="3" rx="1.5" fill="#e2e8f0" />
    <circle cx="440" cy="257" r="3" fill="#0891B2" />
    <rect x="446" y="255" width="30" height="3" rx="1.5" fill="#e2e8f0" />
    <circle cx="440" cy="266" r="3" fill="#22c55e" />
    <rect x="446" y="264" width="30" height="3" rx="1.5" fill="#e2e8f0" />
    {/* Journal entries table */}
    <rect x="118" y="292" width="450" height="40" rx="7" fill="white" stroke="#e2e8f0" strokeWidth="1" />
    <rect x="128" y="300" width="55" height="4" rx="2" fill="#94a3b8" />
    <rect x="240" y="300" width="55" height="4" rx="2" fill="#94a3b8" />
    <rect x="380" y="300" width="55" height="4" rx="2" fill="#94a3b8" />
    <line x1="118" y1="310" x2="568" y2="310" stroke="#f1f5f9" strokeWidth="1" />
    <rect x="128" y="317" width="80" height="4" rx="2" fill="#0F172A" />
    <rect x="240" y="317" width="60" height="4" rx="2" fill="#64748b" />
    <rect x="380" y="317" width="50" height="4" rx="2" fill="#64748b" />
    <rect x="498" y="315" width="48" height="8" rx="4" fill="#22c55e" fillOpacity="0.12" />
    <rect x="509" y="318" width="26" height="3" rx="1.5" fill="#22c55e" />
  </svg>
);

// ── Step type ─────────────────────────────────────────────────────────────────
type Step = 'credentials' | 'company' | 'context' | 'financialYear';

// ── Component ─────────────────────────────────────────────────────────────────
export const LoginPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: Location })?.from?.pathname ?? AppRoutes.DASHBOARD;
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);

  // Changed based on Dinisuru's request: If already authenticated (e.g. after silent refresh),
  // redirect directly to target destination without showing the login form.
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [step,             setStep]             = useState<Step>('credentials');
  const [showPassword,     setShowPassword]     = useState(false);
  const [error,            setError]            = useState<string | null>(null);
  const [isFinalizing,     setIsFinalizing]     = useState(false);

  // ── Login response cache ──────────────────────────────────────────────────
  const [loginData,        setLoginData]        = useState<LoginResponse | null>(null);
  const [formCompanyCode,  setFormCompanyCode]  = useState('');

  // ── Context selections ────────────────────────────────────────────────────
  const [selectedLocationId,   setSelectedLocationId]   = useState<string | null>(null);
  const [selectedCostCenterId, setSelectedCostCenterId] = useState<string | null>(null);

  // ── Financial year state ──────────────────────────────────────────────────
  const [financialYears,  setFinancialYears]  = useState<FinancialYearListDto[]>([]);
  const [selectedFYId,    setSelectedFYId]    = useState<string | null>(null);
  const [isLoadingFYs,    setIsLoadingFYs]    = useState(false);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  // ── Company step state (ENH-002) ──────────────────────────────────────────
  const [companyChoices, setCompanyChoices] = useState<ContextCompany[]>([]);
  const [chosenCompany,  setChosenCompany]  = useState('');
  // Credentials are held only long enough to complete login once the company is known. They are
  // never persisted, and are cleared on success/failure with the rest of the form state.
  const [pendingCreds,   setPendingCreds]   = useState<LoginForm | null>(null);

  // The same username in several companies: the server will not let the password pick one, so it
  // asks for the company code and the field appears.
  const [needCompanyCode,  setNeedCompanyCode]  = useState(false);
  const [companyCodeInput, setCompanyCodeInput] = useState('');

  // ── Helpers ───────────────────────────────────────────────────────────────

  /** Derived context lists from the cached login response */
  const accessibleLocations   = loginData?.accessibleLocations   ?? [];
  const accessibleCostCenters = loginData?.accessibleCostCenters ?? [];

  /** True when context step needs to be shown (multiple options in either dimension) */
  const needsContextStep = (locs: ContextLocation[], ccs: ContextCostCenter[]) =>
    locs.length > 1 || ccs.length > 1;

  // ── Step 1 — Submit credentials ───────────────────────────────────────────
  /**
   * Step 1 — verify the credentials and find out which companies they open.
   *
   * The password does the work the company code used to do: usernames are unique per company, so
   * every row with this username is a candidate and only the ones the password opens come back.
   * One company means there is nothing to ask, so we sign straight in — a control that offers a
   * single choice is not a choice.
   */
  const onSubmit = async (values: LoginForm) => {
    setError(null);
    if (needCompanyCode) {
      const cc = companyCodeInput.trim().toUpperCase();
      if (!cc) { setError('Enter your company code.'); return; }
      await completeLogin(values, cc);
      return;
    }
    dispatch(loginStart());

    try {
      // No company code: the server resolves it from the credentials and, when they open exactly
      // one company, signs in on this single call. This previously asked /auth/companies first and
      // then signed in — two round trips, each running the same deliberately expensive password
      // hash, which is what made "Signing in…" sit there for about two seconds.
      const res = await authApi.login({ username: values.username, password: values.password });

      if (!res.data.success || !res.data.data) {
        dispatch(loginFailure());
        setError(res.data.message || 'Invalid credentials. Please try again.');
        return;
      }

      const data = res.data.data;

      // Several companies open to these credentials — ask which, then sign in naming it.
      if (data.requiresCompanySelection) {
        const companies = data.accessibleCompanies ?? [];
        setCompanyChoices(companies);
        setPendingCreds(values);
        setChosenCompany(companies[0]?.code ?? '');
        dispatch(loginFailure());   // clears the loading flag; not an error state
        setStep('company');
        return;
      }

      await applyLoginResult(data, data.accessibleCompanies?.[0]?.code ?? '');
    } catch (err) {
      dispatch(loginFailure());
      // Surface the server's message — a locked account returns "Your account is temporarily
      // locked…", which the user needs to see instead of a generic credential error (QA #79).
      const axErr = err as AxiosError<{ message?: string; errors?: { code?: string }[] }>;
      if (axErr?.response?.data?.errors?.[0]?.code === 'SEC_ERR_COMPANY_CODE_REQUIRED') setNeedCompanyCode(true);
      setError(axErr?.response?.data?.message ?? 'Unable to reach the server. Please try again.');
    }
  };

  // ── Step 2 — company chosen (or the only one) → sign in ───────────────────
  const completeLogin = async (values: LoginForm, compCode: string) => {
    setError(null);
    setFormCompanyCode(compCode);
    dispatch(loginStart());

    try {
      const res = await authApi.login({ ...values, companyCode: compCode });
      if (!res.data.success || !res.data.data) {
        dispatch(loginFailure());
        setError(res.data.message || 'Invalid credentials. Please try again.');
        return;
      }

      await applyLoginResult(res.data.data, compCode);
    } catch (err) {
      dispatch(loginFailure());
      // Surface the server's message — a locked account returns "Your account is temporarily
      // locked…", which the user needs to see instead of a generic credential error (QA #79).
      const axErr = err as AxiosError<{ message?: string }>;
      setError(axErr?.response?.data?.message ?? 'Authentication failed. Check your credentials and try again.');
    }
  };

  /**
   * The shared tail of a successful sign-in — reached either from the single-call path (no company
   * named) or after the operator picked one. Decides whether the context and financial-year steps
   * are needed.
   */
  const applyLoginResult = async (data: LoginResponse, compCode: string) => {
    // Only after the credentials proved good. Storing on failure would let anyone at the
    // keyboard probe which company codes exist.
    if (compCode) {
      setFormCompanyCode(compCode);
    }
    setLoginData(data);

    const locs = data.accessibleLocations   ?? [];
    const ccs  = data.accessibleCostCenters ?? [];

    // Auto-select if only one option. The location is decided first: the cost centre belongs to
    // it, so choosing one before knowing the location can land on another branch's (ML-02).
    const autoLoc = locs.length === 1 ? locs[0] : (locs.find(l => l.isDefault) ?? null);
    const autoCC  = defaultCostCentreFor(ccs, autoLoc?.id ?? null);
    setSelectedLocationId(autoLoc?.id ?? null);
    setSelectedCostCenterId(autoCC?.id ?? null);

    if (needsContextStep(locs, ccs)) {
      setStep('context');
    } else {
      // Both dimensions have 0 or 1 option — skip context step, go to FY.
      // Pass autoLoc/autoCC directly because React state hasn't flushed yet.
      await loadFinancialYears(data.accessToken, autoLoc ?? null, autoCC ?? null, data);
    }
  };

  // ── Load financial years ──────────────────────────────────────────────────
  // preselectLoc / preselectCC let callers bypass stale React state when the
  // context step is skipped (auto-selection path doesn't flush state in time).
  const loadFinancialYears = async (
    token:        string,
    preselectLoc: ContextLocation | null = null,
    preselectCC:  ContextCostCenter | null = null,
    // Threaded through for the same stale-state reason — see finalizeLogin.
    loginDataOverride: LoginResponse | null = null,
  ) => {
    setIsLoadingFYs(true);
    try {
      const fyRes = await apiClient.get(
        '/financial-years?pageSize=20&sortBy=yearCode&sortDescending=true',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const items: FinancialYearListDto[] = fyRes.data?.data?.items ?? [];
      setFinancialYears(items);
      const currentFY = items.find(y => y.isCurrent) ?? items[0] ?? null;
      setSelectedFYId(currentFY?.id ?? null);

      if (items.length > 0) {
        setStep('financialYear');
        // Store preselected context so onContinueFinancialYear can read it
        if (preselectLoc) setSelectedLocationId(preselectLoc.id);
        if (preselectCC)  setSelectedCostCenterId(preselectCC.id);
      } else {
        // No financial years — finalize immediately with no FY context
        await finalizeLogin(null, null, null, preselectLoc, preselectCC, loginDataOverride);
      }
    } catch {
      // FY load failure is non-fatal — finalize without FY
      await finalizeLogin(null, null, null, preselectLoc, preselectCC, loginDataOverride);
    } finally {
      setIsLoadingFYs(false);
    }
  };

  // ── Step 2 — Continue from context selection → FY ─────────────────────────
  const onContinueContext = async () => {
    if (!loginData) return;
    // By this point state HAS updated (user interacted with the context UI),
    // so no need to pass preselect values.
    await loadFinancialYears(loginData.accessToken);
  };

  // ── Step 3 — Continue from FY selection → finalize ────────────────────────
  const onContinueFinancialYear = async () => {
    const fy = financialYears.find(y => y.id === selectedFYId) ?? null;
    // State is now current (user clicked the FY step), read from it normally.
    const selLoc = accessibleLocations.find(l => l.id === selectedLocationId) ?? null;
    const selCC  = accessibleCostCenters.find(c => c.id === selectedCostCenterId) ?? null;
    await finalizeLogin(fy?.id ?? null, fy?.yearCode ?? null, fy?.description ?? null, selLoc, selCC);
  };

  // ── Finalize — call select-context, dispatch enriched auth + session ──────
  const finalizeLogin = async (
    fyId:          string | null,
    fyCode:        string | null,
    fyDescription: string | null,
    preselectLoc:  ContextLocation | null = null,
    preselectCC:   ContextCostCenter | null = null,
    // Same reason as preselectLoc/preselectCC above: on the auto-select path this runs in the
    // same tick as setLoginData, so the state has not flushed and `loginData` is still null.
    // Without this the function returned silently and login simply stopped — which is what
    // happened for any company with no financial years. Unreachable before ENH-002, because you
    // could only log into your home company; reachable now that you can pick one.
    loginDataOverride: LoginResponse | null = null,
  ) => {
    const data = loginDataOverride ?? loginData;
    if (!data) return;
    setIsFinalizing(true);
    setError(null);

    try {
      // Use directly-passed preselect values (bypass stale state on auto-select
      // path) and fall back to state values when coming from user-driven steps.
      const selLoc = preselectLoc ?? accessibleLocations.find(l => l.id === selectedLocationId) ?? null;
      const selCC  = preselectCC  ?? accessibleCostCenters.find(c => c.id === selectedCostCenterId) ?? null;

      const payload: SelectContextRequest = {
        locationId:        selLoc?.id       || undefined,
        locationCode:      selLoc?.code     || undefined,
        locationName:      selLoc?.name     || undefined,
        costCenterId:      selCC?.id        || undefined,
        costCenterCode:    selCC?.code      || undefined,
        costCenterName:    selCC?.name      || undefined,
        financialYearId:   fyId             || undefined,
        financialYearCode: fyCode           || undefined,
      };

      // Call select-context with the temporary token from initial login
      const enrichedRes = await apiClient.post<{ success: boolean; data?: LoginResponse; message?: string }>(
        '/auth/select-context',
        payload,
        { headers: { Authorization: `Bearer ${data.accessToken}` } },
      );

      if (!enrichedRes.data.success || !enrichedRes.data.data) {
        setError(enrichedRes.data.message || 'Failed to apply context. Please try again.');
        return;
      }

      const enriched = enrichedRes.data.data;

      // Prefer the locally-resolved selLoc/selCC over the echoed JWT claims —
      // the backend echoes back what we sent, so using local data avoids a
      // round-trip inconsistency when the enriched token omits these fields
      // (e.g. for SUPER_ADMIN where the backend may not embed location in JWT).
      dispatch(setSession({
        companyId:    enriched.user.companyId,
        companyCode:  formCompanyCode,
        companyName:  formCompanyCode,
        companyCurrency: enriched.user.baseCurrencyCode ?? 'USD',

        locationId:   selLoc?.id   ?? enriched.user.locationId   ?? null,
        locationCode: selLoc?.code ?? enriched.user.locationCode ?? null,
        locationName: selLoc?.name ?? enriched.user.locationName ?? null,

        costCenterId:   selCC?.id   ?? enriched.user.costCenterId   ?? null,
        costCenterCode: selCC?.code ?? enriched.user.costCenterCode ?? null,
        costCenterName: selCC?.name ?? enriched.user.costCenterName ?? null,

        financialYearId:          fyId,
        financialYearCode:        fyCode,
        financialYearDescription: fyDescription,
      }));

      dispatch(loginSuccess(enriched));

      // Changed based on Dinisuru's request: Notify other open tabs so they do not post to an old company
      broadcastCompanyChange(
        enriched.user.companyId,
        formCompanyCode,
        formCompanyCode
      );

      // Force password change: redirect before letting the user into the app
      if (enriched.user.forcePasswordChange) {
        navigate(AppRoutes.CHANGE_PASSWORD, { replace: true });
        return;
      }

      navigate(from, { replace: true });
    } catch {
      setError('Failed to apply context. Please try again.');
    } finally {
      setIsFinalizing(false);
    }
  };

  // ── Input styles ──────────────────────────────────────────────────────────
  const inputCls =
    'w-full py-2.5 rounded-lg text-sm text-white placeholder-slate-500 outline-none ' +
    'transition-all duration-150 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/60';
  const inputStyle = {
    background:  'rgba(255,255,255,0.07)',
    border:      '1px solid rgba(255,255,255,0.11)',
    paddingLeft: '2.25rem',
  };

  // ── Reusable option button style ──────────────────────────────────────────
  const optionStyle = (isSelected: boolean) => ({
    background: isSelected ? 'rgba(59,130,246,0.14)' : 'rgba(255,255,255,0.04)',
    border:     `1px solid ${isSelected ? 'rgba(59,130,246,0.45)' : 'rgba(255,255,255,0.08)'}`,
  });
  const optionIconStyle = (isSelected: boolean) => ({
    background: isSelected ? 'rgba(59,130,246,0.25)' : 'rgba(255,255,255,0.07)',
  });

  // ── Step dots ─────────────────────────────────────────────────────────────
  const stepIndex = step === 'credentials' ? 0 : step === 'company' ? 1 : step === 'context' ? 2 : 3;

  return (
    <div className="w-full h-screen overflow-hidden flex" style={{ fontFamily: 'Sora, Inter, sans-serif' }}>

      {/* ══════════════════════════════════════════════════════════════════════
          LEFT PANEL — Brand / Visual Storytelling
      ══════════════════════════════════════════════════════════════════════ */}
      <div
        className="hidden lg:flex lg:w-[58%] xl:w-[60%] flex-col justify-between p-5 xl:p-7 relative overflow-hidden select-none"
        style={{ background: 'linear-gradient(145deg, #f8fafc 0%, #f0f4f8 55%, #e8eef5 100%)' }}
      >
        {/* Grid overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(148,163,184,0.18) 1px, transparent 1px),' +
              'linear-gradient(90deg, rgba(148,163,184,0.18) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
          }}
        />
        {/* Radial glow */}
        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 65%)' }}
        />

        {/* Wordmark — myPOS ONE brand */}
        <div className="relative z-10 flex items-center gap-2.5">
          <BrandCube size={40} dark={false} />
          <div className="leading-none">
            <span className="text-[15px] font-extrabold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.02em' }}>
              myLedger <span style={{ color: '#2563EB' }}>ONE</span>
            </span>
            <span className="block text-[9px] font-semibold text-slate-400 uppercase tracking-[0.14em] mt-0.5">
              Omnichannel · NextGen · Ecosystem
            </span>
          </div>
        </div>

        {/* Hero + Dashboard */}
        <div className="relative z-10 flex flex-col">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-2 w-fit"
            style={{ background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.15)' }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#2563EB' }} />
            <span className="text-[11px] font-semibold tracking-wide" style={{ color: '#2563EB' }}>
              General Ledger Module of myPOS ONE
            </span>
          </div>
          <h1 className="text-2xl xl:text-[2rem] font-extrabold leading-[1.15] mb-1.5 tracking-tight"
            style={{ color: '#0F172A', letterSpacing: '-0.025em' }}>
            Financial Intelligence<br />
            <span style={{ color: '#2563EB' }}>for the Enterprise.</span>
          </h1>
          <p className="text-[13px] leading-snug max-w-md mb-3" style={{ color: '#475569' }}>
            The GL module of myPOS ONE. Multi-company, multi-currency accounting with
            real-time P&L, workflow approvals, and full audit control — deeply integrated
            with POS, AR, AP, INV, CRM, and the entire ecosystem.
          </p>
          <div className="rounded-xl overflow-hidden shadow-[0_8px_40px_rgba(15,23,42,0.12)] ring-1 ring-slate-200/70 max-w-[500px]" style={{ maxHeight: '190px' }}>
            <DashboardMockup />
          </div>
        </div>

        {/* Feature cards — brand Signal Blue / Teal accent */}
        <div className="relative z-10 grid grid-cols-3 gap-1.5 mt-3">
          {FEATURES.map(({ icon: Icon, title, desc }, i) => (
            <div
              key={title}
              className="flex items-start gap-2 backdrop-blur-sm rounded-lg px-2.5 py-2"
              style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(226,232,240,0.7)' }}
            >
              <div
                className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ background: i % 2 === 0 ? 'rgba(37,99,235,0.09)' : 'rgba(8,145,178,0.09)' }}
              >
                <Icon size={13} style={{ color: i % 2 === 0 ? '#2563EB' : '#0891B2' }} />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] font-semibold leading-tight truncate" style={{ color: '#0F172A' }}>{title}</div>
                <div className="text-[10px] leading-tight mt-0.5 truncate" style={{ color: '#64748b' }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Module strip — myPOS ONE ecosystem nav */}
        <div className="relative z-10 flex items-center gap-2.5 mt-2">
          <span className="text-[9px] font-bold uppercase tracking-[0.18em] whitespace-nowrap" style={{ color: '#94a3b8' }}>
            Ecosystem
          </span>
          <div className="flex gap-1.5 flex-wrap">
            {MODULES.map((mod) => (
              <span
                key={mod}
                className="text-[10px] font-bold px-2 py-0.5 rounded-md"
                style={
                  mod === 'GL'
                    ? { background: '#2563EB', color: '#fff', boxShadow: '0 1px 4px rgba(37,99,235,0.35)' }
                    : { background: 'rgba(255,255,255,0.88)', color: '#475569', border: '1px solid rgba(203,213,225,0.8)' }
                }
              >
                {mod}
              </span>
            ))}
          </div>
          <span className="text-[9px] font-semibold whitespace-nowrap" style={{ color: '#94a3b8' }}>& more</span>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          RIGHT PANEL — Authentication
      ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 flex flex-col h-screen" style={{ background: '#0c1628' }}>
        {/* Top accent bar */}
        <div
          className="h-[3px] flex-shrink-0"
          style={{ background: 'linear-gradient(90deg, #1d4ed8 0%, #3b82f6 50%, #60a5fa 100%)' }}
        />

        {/* Center content */}
        <div className="flex-1 flex items-center justify-center px-6 py-2 sm:px-8 lg:px-10 overflow-y-auto min-h-0">
          <div className="w-full max-w-[340px]">

            {/* Brand mark — only shown on credentials step to save vertical space on steps 2+3 */}
            {step === 'credentials' && (
              <div className="flex flex-col items-center mb-5">
                {/* Dimensional cube on dark — right face uses Slate for visibility */}
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-2.5"
                  style={{
                    background: 'rgba(15,23,42,0.6)',
                    boxShadow: '0 0 0 8px rgba(37,99,235,0.10), 0 8px 28px rgba(0,0,0,0.45)',
                    border: '1px solid rgba(37,99,235,0.2)',
                  }}
                >
                  <BrandCube size={48} dark={true} />
                </div>
                <div className="text-center">
                  <div className="text-white font-extrabold text-[18px] tracking-tight" style={{ letterSpacing: '-0.02em' }}>
                    myLedger <span style={{ color: '#3B82F6' }}>ONE</span>
                  </div>
                  <div className="text-[11px] mt-0.5 uppercase tracking-[0.12em]" style={{ color: '#475569' }}>
                    Omnichannel · NextGen · Ecosystem
                  </div>
                </div>
              </div>
            )}

            {/* Compact brand line for steps 2 and 3 */}
            {step !== 'credentials' && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <BrandCube size={22} dark={true} />
                <span className="font-extrabold text-[13px] tracking-tight text-white" style={{ letterSpacing: '-0.01em' }}>
                  myLedger <span style={{ color: '#3B82F6' }}>ONE</span>
                </span>
              </div>
            )}

            {/* Step progress dots */}
            <div className={`flex items-center justify-center gap-2 ${step === 'credentials' ? 'mb-5' : 'mb-4'}`}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-[3px] rounded-full transition-all duration-300"
                  style={{
                    width:      stepIndex === i ? '28px' : '16px',
                    background: stepIndex === i
                      ? '#3b82f6'
                      : stepIndex > i
                        ? '#1e3a5f'
                        : 'rgba(255,255,255,0.08)',
                  }}
                />
              ))}
            </div>

            {/* ══════════════════════════════════════════════════════════════
                STEP 1 — Credentials
            ══════════════════════════════════════════════════════════════ */}
            {step === 'credentials' && (
              <div className="animate-slide-up">
                <div className="text-center mb-5">
                  <h2 className="text-[20px] font-bold text-white mb-1">Welcome Back!</h2>
                  <p className="text-[13px]" style={{ color: '#64748b' }}>Sign in to access myLedger ONE</p>
                </div>

                {error && (
                  <div
                    className="flex items-start gap-2.5 rounded-lg p-3 mb-5 text-[13px]"
                    style={{ background: 'rgba(239,68,68,0.09)', border: '1px solid rgba(239,68,68,0.22)', color: '#fca5a5' }}
                  >
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
                  {/* Company is normally NOT asked here — see the company step below. Only when the
                      username exists in several companies does the server ask for it. */}
                  {needCompanyCode && (
                    <div>
                      <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-[0.08em] mb-1.5">
                        Company code
                      </label>
                      <div className="relative">
                        <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                        <input
                          value={companyCodeInput}
                          onChange={e => setCompanyCodeInput(e.target.value)}
                          placeholder="e.g. NELNA"
                          autoComplete="organization"
                          autoFocus
                          className={inputCls}
                          style={inputStyle}
                        />
                      </div>
                    </div>
                  )}

                  {/* Username */}
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-[0.08em] mb-1.5">
                      Username
                    </label>
                    <div className="relative">
                      <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                      <input
                        {...register('username')}
                        placeholder="Enter your username"
                        autoComplete="username"
                        className={inputCls}
                        style={inputStyle}
                      />
                    </div>
                    {errors.username && (
                      <p className="text-red-400 text-[11px] mt-1">{errors.username.message}</p>
                    )}
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-[0.08em] mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                      <input
                        {...register('password')}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        autoComplete="current-password"
                        className={inputCls}
                        style={{ ...inputStyle, paddingRight: '2.5rem' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-300 transition-colors"
                        tabIndex={-1}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-red-400 text-[11px] mt-1">{errors.password.message}</p>
                    )}
                  </div>

                  {/* Remember me + Forgot Password */}
                  <div className="flex items-center justify-between pt-0.5">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        className="w-3.5 h-3.5 rounded border-white/20 bg-white/10 accent-blue-500 cursor-pointer"
                      />
                      <span className="text-[12px] text-slate-400 group-hover:text-slate-300 transition-colors select-none">
                        Remember me
                      </span>
                    </label>
                    <Link
                      to={AppRoutes.FORGOT_PASSWORD}
                      className="text-[12px] text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Forgot Password?
                    </Link>
                  </div>

                  {/* Sign In */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-2 py-[11px] rounded-lg text-[14px] font-semibold text-white transition-all duration-150 hover:brightness-110 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed mt-1 shadow-lg"
                    style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 50%, #3b82f6 100%)' }}
                  >
                    {isSubmitting ? (
                      <><Loader2 size={15} className="animate-spin" /> Signing in…</>
                    ) : (
                      'Sign In'
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                STEP 2 — Location + Cost Center (dropdown style)
            ══════════════════════════════════════════════════════════════ */}
            {/* ── Step 2 — Company (ENH-002) ─────────────────────────────────────
                Only reached when the credentials open MORE THAN ONE company. A single-company
                user never sees this: they are signed straight in, because a picker with one
                entry is a speed bump, not a choice. */}
            {step === 'company' && (
              <div className="animate-slide-up">
                <div className="text-center mb-4">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-lg"
                    style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.25)' }}
                  >
                    <Building2 size={17} className="text-blue-400" />
                  </div>
                  <h2 className="text-[17px] font-bold text-white">Select Company</h2>
                  <p className="text-[12px] text-slate-400 mt-0.5">
                    Your sign-in works for more than one company
                  </p>
                </div>

                {error && (
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-[12px]">
                    <AlertCircle size={14} className="shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  {companyChoices.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setChosenCompany(c.code)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${
                        chosenCompany === c.code
                          ? 'border-blue-500/60 bg-blue-500/10'
                          : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold text-white truncate">{c.name}</p>
                          <p className="text-[11px] text-slate-400 font-mono">{c.code} · {c.baseCurrencyCode}</p>
                        </div>
                        {chosenCompany === c.code && <CheckCircle2 size={15} className="text-blue-400 shrink-0" />}
                      </div>
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  disabled={!chosenCompany || isSubmitting}
                  onClick={() => pendingCreds && completeLogin(pendingCreds, chosenCompany)}
                  className="w-full flex items-center justify-center gap-2 py-[11px] rounded-lg text-[14px] font-semibold text-white transition-all duration-150 hover:brightness-110 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed mt-4 shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 50%, #3b82f6 100%)' }}
                >
                  {isSubmitting ? (<><Loader2 size={15} className="animate-spin" /> Signing in…</>) : 'Continue'}
                </button>

                <button
                  type="button"
                  onClick={() => { setStep('credentials'); setPendingCreds(null); setError(null); }}
                  className="w-full text-[12px] text-slate-400 hover:text-slate-300 transition-colors mt-2"
                >
                  Back
                </button>
              </div>
            )}

            {step === 'context' && (
              <div className="animate-slide-up">
                {/* Header */}
                <div className="text-center mb-4">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-lg"
                    style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.25)' }}
                  >
                    <Layers size={16} className="text-blue-400" />
                  </div>
                  <h2 className="text-[18px] font-bold text-white mb-0.5">Select Context</h2>
                  <p className="text-slate-400 text-[12px]">Choose your operating location and cost center</p>
                </div>

                {error && (
                  <div
                    className="flex items-start gap-2.5 rounded-lg p-3 mb-4 text-[13px]"
                    style={{ background: 'rgba(239,68,68,0.09)', border: '1px solid rgba(239,68,68,0.22)', color: '#fca5a5' }}
                  >
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                {/* ── Location dropdown ── */}
                {accessibleLocations.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-[0.08em] mb-1.5 flex items-center gap-1.5">
                      <MapPin size={11} />
                      Location
                    </label>
                    <div className="relative">
                      <select
                        value={selectedLocationId ?? ''}
                        onChange={e => {
                          const locId = e.target.value || null;
                          setSelectedLocationId(locId);
                          // The cost centre belongs to the location, so changing the location
                          // re-decides it. Null when the new location has none, rather than
                          // leaving the previous location's cost centre selected.
                          setSelectedCostCenterId(
                            defaultCostCentreFor(accessibleCostCenters, locId)?.id ?? null);
                        }}
                        disabled={accessibleLocations.length === 1}
                        className="w-full px-3 py-2.5 pr-9 rounded-lg text-[13px] text-white outline-none appearance-none transition-all duration-150 focus:ring-2 focus:ring-blue-500/50"
                        style={{
                          background:  'rgba(255,255,255,0.07)',
                          border:      '1px solid rgba(255,255,255,0.11)',
                        }}
                      >
                        {accessibleLocations.map(loc => (
                          <option key={loc.id} value={loc.id} style={{ background: '#1e293b', color: '#fff' }}>
                            {loc.code} — {loc.name}{loc.city ? ` (${loc.city})` : ''}
                          </option>
                        ))}
                      </select>
                      <ChevronRight
                        size={14}
                        className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 text-slate-500 pointer-events-none"
                      />
                    </div>
                    {accessibleLocations.length === 1 && (
                      <p className="text-[10px] text-slate-600 mt-1 pl-1">Only one location available — auto-selected</p>
                    )}
                  </div>
                )}

                {/* ── Cost Center dropdown ── */}
                {(() => {
                  // Only the selected location's cost centres, and never an untagged one (ML-02).
                  const filteredCCs = costCentresForLocation(accessibleCostCenters, selectedLocationId);
                  if (filteredCCs.length === 0) return null;
                  return (
                    <div className="mb-6">
                      <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-[0.08em] mb-1.5 flex items-center gap-1.5">
                        <Layers size={11} />
                        Cost Center
                      </label>
                      <div className="relative">
                        <select
                          value={selectedCostCenterId ?? ''}
                          onChange={e => setSelectedCostCenterId(e.target.value || null)}
                          disabled={filteredCCs.length === 1}
                          className="w-full px-3 py-2.5 pr-9 rounded-lg text-[13px] text-white outline-none appearance-none transition-all duration-150 focus:ring-2 focus:ring-blue-500/50"
                          style={{
                            background:  'rgba(255,255,255,0.07)',
                            border:      '1px solid rgba(255,255,255,0.11)',
                          }}
                        >
                          {filteredCCs.map(cc => (
                            <option key={cc.id} value={cc.id} style={{ background: '#1e293b', color: '#fff' }}>
                              {cc.code} — {cc.name}
                            </option>
                          ))}
                        </select>
                        <ChevronRight
                          size={14}
                          className="absolute right-3 top-1/2 -translate-y-1/2 rotate-90 text-slate-500 pointer-events-none"
                        />
                      </div>
                      {filteredCCs.length === 1 && (
                        <p className="text-[10px] text-slate-600 mt-1 pl-1">Only one cost center available — auto-selected</p>
                      )}
                    </div>
                  );
                })()}

                {/* Continue */}
                <button
                  onClick={onContinueContext}
                  disabled={isLoadingFYs}
                  className="w-full flex items-center justify-center gap-2 py-[11px] rounded-lg text-[14px] font-semibold text-white transition-all duration-150 hover:brightness-110 active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed mb-3 shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)' }}
                >
                  {isLoadingFYs ? (
                    <><Loader2 size={15} className="animate-spin" /> Loading financial years…</>
                  ) : (
                    <>Continue <ChevronRight size={15} /></>
                  )}
                </button>

                {/* Back */}
                <button
                  onClick={() => { setStep('credentials'); setError(null); }}
                  className="w-full flex items-center justify-center gap-1.5 py-2 text-[12px] text-slate-500 hover:text-slate-300 transition-colors"
                >
                  <ArrowLeft size={12} />
                  Back to Sign In
                </button>
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                STEP 3 — Financial Year Selection
            ══════════════════════════════════════════════════════════════ */}
            {step === 'financialYear' && (
              <div className="animate-slide-up">
                <div className="text-center mb-4">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center mx-auto mb-2 shadow-lg"
                    style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.25)' }}
                  >
                    <CalendarRange size={16} className="text-blue-400" />
                  </div>
                  <h2 className="text-[18px] font-bold text-white mb-0.5">Select Financial Year</h2>
                  <p className="text-slate-400 text-[12px]">Choose the accounting period to work in</p>
                </div>

                {error && (
                  <div
                    className="flex items-start gap-2.5 rounded-lg p-3 mb-4 text-[13px]"
                    style={{ background: 'rgba(239,68,68,0.09)', border: '1px solid rgba(239,68,68,0.22)', color: '#fca5a5' }}
                  >
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                {/* FY list */}
                <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-0.5 mb-4 scrollbar-thin">
                  {financialYears.map(fy => {
                    const isSel  = selectedFYId === fy.id;
                    const isOpen = fy.yearEndStatus === 'OPEN';
                    return (
                      <button
                        key={fy.id}
                        onClick={() => setSelectedFYId(fy.id)}
                        className="w-full flex items-center gap-2.5 p-2.5 rounded-lg text-left transition-all duration-150"
                        style={optionStyle(isSel)}
                      >
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={optionIconStyle(isSel)}
                        >
                          <CalendarRange size={13} className={isSel ? 'text-blue-300' : 'text-slate-500'} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-white truncate leading-tight">
                            {fy.yearCode}
                            {fy.isCurrent && (
                              <span className="ml-2 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
                                style={{ background: 'rgba(34,197,94,0.15)', color: '#86efac' }}>
                                Current
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 truncate mt-0.5">
                            {fy.description} · {fy.startDate.substring(0, 10)} – {fy.endDate.substring(0, 10)}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span
                            className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
                            style={isOpen
                              ? { background: 'rgba(34,197,94,0.12)', color: '#86efac' }
                              : { background: 'rgba(100,116,139,0.15)', color: '#94a3b8' }}
                          >
                            {isOpen ? 'Open' : fy.yearEndStatus.replace('_', ' ')}
                          </span>
                          {isSel && <CheckCircle2 size={15} className="text-blue-400" />}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Enter Dashboard */}
                <button
                  onClick={onContinueFinancialYear}
                  disabled={!selectedFYId || isFinalizing}
                  className="w-full flex items-center justify-center gap-2 py-[11px] rounded-lg text-[14px] font-semibold text-white transition-all duration-150 hover:brightness-110 active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed mb-3 shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)' }}
                >
                  {isFinalizing ? (
                    <><Loader2 size={15} className="animate-spin" /> Applying context…</>
                  ) : (
                    <>Enter Dashboard <ChevronRight size={15} /></>
                  )}
                </button>

                {/* Back */}
                <button
                  onClick={() => setStep(needsContextStep(accessibleLocations, accessibleCostCenters) ? 'context' : 'credentials')}
                  className="w-full flex items-center justify-center gap-1.5 py-2 text-[12px] text-slate-500 hover:text-slate-300 transition-colors"
                >
                  <ArrowLeft size={12} />
                  Back
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 pb-3 pt-1 text-center space-y-0.5 px-4">
          <p className="text-[10px] tracking-wide" style={{ color: '#334155' }}>
            Part of the myPOS ONE Enterprise Ecosystem
          </p>
          <p className="text-[11px]" style={{ color: '#475569' }}>
            © 2026 myPOS Software Solutions (Pvt) Ltd. All Rights Reserved.
          </p>
        </div>
      </div>
    </div>
  );
};
